import {Progress} from '@p4b/utils-progress';
import {translate} from '@p4b/utils-lang';
import {isIndexed, safeJsonParse, wait} from '@p4b/utils';
import {version} from '@gen/version';
import {dbGet, dbPut} from '@p4b/utils-db';

// Domain constants
export const apiDomain = 'https://dev-api.assess.risr.global';
export const ingestDomain = 'https://dev-ingest.assess.risr.global';
export const currentDomain = window.location.hostname;

// Org config — fetched once on first call, cached for the app lifetime
let orgPromise: Promise<string> | null = null;

export function getOrg(): Promise<string> {
    if (!orgPromise) {
        orgPromise = fetch(`${apiDomain}/config/${currentDomain}/`)
            .then(r => {
                if (!r.ok) throw new HttpError(r.status, r.statusText);
                return r.json();
            })
            .then((j: unknown) => {
                if (isIndexed(j) && typeof j.org === 'string') return j.org;
                throw new Error(`Org config response missing 'org' field`);
            });
    }
    return orgPromise;
}

// Remote Logging

export function isRemoteLogging(x: unknown): x is {send_logs: boolean} {
    return isIndexed(x) && typeof x.send_logs === 'boolean';
}

enum LogLevel {
    DEBUG = 5,
    INFO = 4,
    LOG = 3,
    WARN = 2,
    ERROR = 1,
    NONE = 0,
}

const logLevel = LogLevel.DEBUG;
const logVault = '/logvault/';
const {debug, info, log, warn, error} = console;
let remote_logging_enabled = false;

export async function enableRemoteLogging(enabled?: boolean): Promise<void> {
    if (enabled === undefined) {
        enabled = await dbGet<boolean>('session', 'send-logs') === true;
    }
    if (enabled === true && remote_logging_enabled === false) {
        console.debug = remoteDebug;
        console.info = remoteInfo;
        console.log = remoteLog;
        console.warn = remoteWarn;
        console.error = remoteError;
        remoteInfo('Remote logging enabled');
        remote_logging_enabled = true;
        await dbPut('session', true, 'send-logs');
    } else if (enabled === false && remote_logging_enabled === true) {
        remoteInfo('Remote logging disabled');
        console.debug = debug;
        console.info = info;
        console.log = log;
        console.warn = warn;
        console.error = error;
        remote_logging_enabled = false;
        await dbPut('session', false, 'send-logs');
    }
}

export function logger(level: string, timestamp: number, args: unknown[]): void {
    (async () => {
        if (isNetworkBlocked()) {
            return;
        }
        try {
            await requestJson('', logVault, [level, timestamp.toFixed(0), ...args]);
        } catch {}
    })();
}

function remoteDebug(...args: unknown[]): void {
    if (logLevel >= LogLevel.DEBUG) {
        debug(JSON.stringify(args));
        logger('DEBUG', performance.now(), args);
    }
}

function remoteInfo(...args: unknown[]): void {
    if (logLevel >= LogLevel.INFO) {
        info(JSON.stringify(args));
        logger(' INFO', performance.now(), args);
    }
}

function remoteLog(...args: unknown[]): void {
    if (logLevel >= LogLevel.LOG) {
        log(JSON.stringify(args));
        logger('  LOG', performance.now(), args);
    }
}

function remoteWarn(...args: unknown[]): void {
    if (logLevel >= LogLevel.WARN) {
        warn(JSON.stringify(args));
        logger(' WARN', performance.now(), args);
    }
}

function remoteError(...args: unknown[]): void {
    if (logLevel >= LogLevel.ERROR) {
        error(JSON.stringify(args));
        logger('ERROR', performance.now(), args);
    }
}

// Safe Exam Browser integration

declare global {
    interface Window {
        SafeExamBrowser: {
            version: string;
            security: {
                configKey: string;
                browserExamKey: string;
                updateKeys(callback: () => void): void;
            }
        }
    }
}

const safeExamBrowser = window.SafeExamBrowser != null

// Login credentials

let loginId = "";
let loginPwd = "";

export function setCredentials(args: {loginId: string; loginPwd: string}): void {
    //admin = false;
    loginId = args.loginId;
    loginPwd = args.loginPwd;
}

export function clearCredentials(): void {
    //admin = false;
    loginId = "";
    loginPwd = "";
}

// Device UUID

let uuid = "";

export function setUUID(args: {uuid: string}): void {
    uuid = args.uuid;
}

// HTTP Errors

export class HttpError extends Error {
    constructor(public readonly status: number, message: string) {
        super(message);
        Object.defineProperty(this, 'name', { value: 'HttpError' });
        Object.setPrototypeOf(this, new.target.prototype);
        Error?.captureStackTrace(this, HttpError);
    }
}

export class AbortError extends Error {
    constructor() {
        super('HTTP operation terminated');
        Object.defineProperty(this, 'name', { value: 'AbortError' });
        Object.setPrototypeOf(this, new.target.prototype);
        Error?.captureStackTrace(this, AbortError);
    }
}


let failureCoolDownTimer: ReturnType<typeof setTimeout> | null = null;
const FAILURE_COOLDOWN_MS = 5000;

export function isNetworkBlocked(): boolean {
    return failureCoolDownTimer !== null;
}

function setFailureBlock(): void {
    if (failureCoolDownTimer !== null) {
        clearTimeout(failureCoolDownTimer);
    }

    failureCoolDownTimer = setTimeout(() => {
        failureCoolDownTimer = null;
        console.info('Network cool-down period ended. Requests are now allowed.');
    }, FAILURE_COOLDOWN_MS);

    console.warn(`Network failure detected. Blocking further requests for ${FAILURE_COOLDOWN_MS}ms.`);
}

function clearFailureBlock(): void {
    if (failureCoolDownTimer !== null) {
        clearTimeout(failureCoolDownTimer);
        failureCoolDownTimer = null;
    }
}

function cleanup(xhr: XMLHttpRequest): void {
    xhr.onerror = null;
    xhr.ontimeout = null;
    xhr.onabort = null;
    xhr.onload = null;
    xhr.onprogress = null;
}

function setupRequestHeaders(xhr: XMLHttpRequest, start?: number, end?: number): void {
    xhr.setRequestHeader("Cache-Control", "no-cache, no-store, max-age=0");
    if (start !== undefined && end !== undefined) {
        console.debug('range', start, '-', end);
        xhr.setRequestHeader('Range', 'bytes=' + start + '-' + end);
    }
    if (safeExamBrowser) {
        xhr.setRequestHeader(
            'X-SafeExamBrowser-RequestHash',
            window.SafeExamBrowser.security.browserExamKey
        );
        xhr.setRequestHeader(
            'X-SafeExamBrowser-ConfigKeyHash',
            window.SafeExamBrowser.security.configKey
        );
    }
    if (loginId && loginPwd) {
        xhr.setRequestHeader('X-Risr-User', loginId);
        xhr.setRequestHeader('X-Risr-Pass', loginPwd);
    }
    if (uuid) {
        xhr.setRequestHeader('X-Risr-Device-Id', uuid);
    }
    xhr.setRequestHeader('X-Risr-Vers', version);
    xhr.setRequestHeader('X-Risr-Org', currentDomain);
}

export function requestJson<T>(domain: string, url: string, obj: T): Promise<{data?: unknown, req_timestamp?: number, rsp_timestamp?: number}> {
    const req_timestamp = Date.now();
    return new Promise(async (succ, fail) => {
        const xhr = new XMLHttpRequest();
        const handleFailure = (err: unknown): void => {
            cleanup(xhr);
            setFailureBlock();
            fail(err);
        };
        const errorHandler =  (): void => {
            handleFailure(new Error(translate('ERROR_NETWORK')));
        };
        const timeoutHandler = (): void => {
            handleFailure(new Error(translate('ERROR_CONNECTION_TIMEOUT')));
        };
        const abortHandler = (): void => {
            handleFailure(new AbortError());
        };
        const loadHandler = (): void => {
            const rsp_timestamp = Date.now();
            if (xhr.status >= 200 && xhr.status < 300) {
                cleanup(xhr);
                clearFailureBlock();
                if (xhr.responseText) {
                    const data = safeJsonParse(xhr.responseText);
                    succ({data, req_timestamp, rsp_timestamp});
                } else {
                    succ({data: null, req_timestamp, rsp_timestamp});
                }
            } else {
                handleFailure(new HttpError(xhr.status, `${xhr.statusText} "${xhr.responseText}"`));
            }
        }
        try {
            xhr.open('POST', `${domain}${url}`, true);
            xhr.onerror = errorHandler;
            xhr.ontimeout = timeoutHandler;
            xhr.onabort = abortHandler;
            xhr.onload = loadHandler;
            //xhr.timeout = 300000;
            setupRequestHeaders(xhr);
            xhr.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
            xhr.responseType = 'text';
            xhr.send(JSON.stringify(obj));
        } catch (err) {
            handleFailure(err);
        }
    });
}

export function getHead(domain: string, url: string): Promise<string> {
    return new Promise((succ, fail): void => {
        const xhr = new XMLHttpRequest();
        const errorHandler =  (): void => {
            cleanup(xhr);
            fail(new Error(translate('ERROR_NETWORK')));
        };
        const timeoutHandler = (): void => {
            cleanup(xhr);
            fail(new Error(translate('ERROR_CONNECTION_TIMEOUT')));
        };
        const abortHandler = (): void => {
            cleanup(xhr);
            fail(new AbortError());
        };
        const loadHandler = (): void => {
            cleanup(xhr);
            if (xhr.status >= 200 && xhr.status < 300) {
                succ(xhr.getResponseHeader('content-length') || '0');
            } else {
                fail(new HttpError(xhr.status, `${xhr.statusText} "${xhr.responseText}"`));
            }
        };
        try {
            xhr.open('HEAD', `${domain}${url}`, true);
            xhr.onerror = errorHandler;
            xhr.ontimeout = timeoutHandler;
            xhr.onabort = abortHandler;
            xhr.onload = loadHandler;
            //xhr.timeout = 300000;
            setupRequestHeaders(xhr);
            xhr.responseType = 'text';
            xhr.send(null);
        } catch (err) {
            cleanup(xhr);
            fail(err);
        }
    });
}

export function getText(domain: string, url: string): Promise<string> {
    return new Promise((succ, fail): void => {
        const xhr = new XMLHttpRequest();
        const errorHandler =  (): void => {
            cleanup(xhr);
            fail(new Error(translate('ERROR_NETWORK')));
        };
        const timeoutHandler = (): void => {
            cleanup(xhr);
            fail(new Error(translate('ERROR_CONNECTION_TIMEOUT')));
        };
        const abortHandler = (): void => {
            cleanup(xhr);
            fail(new AbortError());
        };
        const loadHandler = (): void => {
            cleanup(xhr);
            if (xhr.status >= 200 && xhr.status < 300) {
                succ(xhr.responseText);
            } else {
                fail(new HttpError(xhr.status, `${xhr.statusText} "${xhr.responseText}"`));
            }
        }
        try {
            xhr.open('GET', `${domain}${url}`, true);
            xhr.onerror = errorHandler;
            xhr.ontimeout = timeoutHandler;
            xhr.onabort = abortHandler;
            xhr.onload = loadHandler;
            //xhr.timeout = 30000;
            setupRequestHeaders(xhr);
            xhr.responseType = 'text';
            xhr.send(null);
        } catch (err) {
            cleanup(xhr);
            fail(err);
        }
    });
}

export function getJson(domain: string, url: string, start?: number, end?: number): Promise<unknown> {
    return new Promise((succ, fail): void => {
        const xhr = new XMLHttpRequest();
        const errorHandler =  (): void => {
            cleanup(xhr);
            fail(new Error(translate('ERROR_NETWORK')));
        }
        const timeoutHandler = (): void => {
            cleanup(xhr);
            fail(new Error(translate('ERROR_CONNECTION_TIMEOUT')));
        }
        const abortHandler = (): void => {
            cleanup(xhr);
            fail(new AbortError());
        }
        const loadHandler = (): void => {
            cleanup(xhr);
            if (xhr.status >= 200 && xhr.status < 300) {
                if (!xhr.responseText) {
                    succ(null);
                } else {
                    succ(safeJsonParse(xhr.responseText));
                }
            } else {
                fail(new HttpError(xhr.status, `${xhr.statusText} "${xhr.responseText}"`));
            }
        }
        try {
            xhr.open('GET', `${domain}${url}`, true);
            xhr.onerror = errorHandler;
            xhr.ontimeout = timeoutHandler;
            xhr.onabort = abortHandler;
            xhr.onload = loadHandler;
            //xhr.timeout = 30000;
            setupRequestHeaders(xhr, start, end);
            xhr.responseType = 'text';
            xhr.send(null);
        } catch (err) {
            cleanup(xhr);
            fail(err);
        }
    });
}

export function httpDelete(domain: string, url: string): Promise<void> {
    return new Promise((succ, fail): void => {
        const xhr = new XMLHttpRequest();
        const errorHandler =  (): void => {
            cleanup(xhr);
            fail(new Error(translate('ERROR_NETWORK')));
        }
        const timeoutHandler = (): void => {
            cleanup(xhr);
            fail(new Error(translate('ERROR_CONNECTION_TIMEOUT')));
        }
        const abortHandler = (): void => {
            cleanup(xhr);
            fail(new AbortError());
        }
        const loadHandler = (): void => {
            cleanup(xhr);
            if (xhr.status >= 200 && xhr.status < 300) {
                succ();
            } else {
                fail(new HttpError(xhr.status, `${xhr.statusText} "${xhr.responseText}"`));
            }
        }
        try {
            xhr.open('DELETE', `${domain}${url}`, true);
            xhr.onerror = errorHandler;
            xhr.ontimeout = timeoutHandler;
            xhr.onabort = abortHandler;
            xhr.onload = loadHandler;
            //xhr.timeout = 30000;
            setupRequestHeaders(xhr);
            xhr.responseType = 'text';
            xhr.send(null);
        } catch (err) {
            cleanup(xhr);
            fail(err);
        }
    });
}

export interface ProgressUi {
    ui: {
        progressBox: HTMLDivElement;
        titleBox: HTMLDivElement;
        subtextBox: HTMLDivElement;
        title: Text;
        subtext: Text;
        spinner: HTMLDivElement;
        textBox: HTMLDivElement;
        text: Text;
    };
    currentSize?: number;
    totalSize?: number;
}

export function rangeSupported(url: string): Promise<{ranged:boolean,size:number}> {
    return new Promise((succ, fail): void => {
        const xhr = new XMLHttpRequest();
        const errorHandler =  (): void => {
            cleanup(xhr);
            fail(new Error(translate('ERROR_NETWORK')));
        };
        const timeoutHandler = (): void => {
            cleanup(xhr);
            fail(new Error(translate('ERROR_CONNECTION_TIMEOUT')));
        };
        const abortHandler = (): void => {
            cleanup(xhr);
            fail(new AbortError());
        };
        const loadHandler = (): void => {
            cleanup(xhr);
            if (xhr.status === 206) {
                const match = xhr.getResponseHeader('content-range')?.match(/\/([0-9]*)$/)?.[1];
                console.debug('MATCH', match);
                const ranged = true;
                const size = parseInt(match || '0');
                succ({ranged, size});
            } else if (xhr.status >= 200 && xhr.status < 300) {
                const ranged = false;
                const size = parseInt(xhr.getResponseHeader('content-length') || '0');
                succ({ranged, size});
            } else {
                fail(new HttpError(xhr.status, xhr.statusText));
            }
        }
        try {
            xhr.open('GET', url, true);
            xhr.onerror = errorHandler;
            xhr.ontimeout = timeoutHandler;
            xhr.onabort = abortHandler;
            xhr.onload = loadHandler;
            //xhr.timeout = 30000;
            setupRequestHeaders(xhr, 0, 1);
            xhr.responseType = 'arraybuffer';
            xhr.send(null);
        } catch (err) {
            cleanup(xhr);
            fail(err);
        }
    });
}

export function getArrayBuffer(url: string, progress: Progress, start?: number, end?: number): Promise<ArrayBuffer> {
    return new Promise((succ, fail): void => {
        const xhr = new XMLHttpRequest();
        const errorHandler =  (): void => {
            cleanup(xhr);
            fail(new Error(translate('ERROR_NETWORK')));
        }
        const timeoutHandler = (): void => {
            console.log('CHARS GOT:', xhr.response.byteLength);
            cleanup(xhr);
            fail(new Error(translate('ERROR_CONNECTION_TIMEOUT')));
        }
        const abortHandler = (): void => {
            cleanup(xhr);
            fail(new AbortError());
        }
        const progressHandler = async (e: ProgressEvent<EventTarget>): Promise<void> => {
            if (e.loaded != null) {
                console.debug(`LENGTH_SO_FAR ${e.loaded}`);
                await progress.show(e.loaded);
            }
        };
        const loadHandler = (): void => {
            cleanup(xhr);
            if (xhr.status === 200 || xhr.status === 206) {
                progress.save(xhr.response.byteLength);
                if (start || end) {
                    console.debug('downloaded exam chunk, start: ', start, ' end: ', start + xhr.response.byteLength);
                } else {
                    console.debug('DOWNLOADED');
                }
                succ(xhr.response);
            } else {
                fail(new HttpError(xhr.status, xhr.statusText));
            }
        }
        try {
            xhr.open('GET', url, true);
            xhr.onerror = errorHandler;
            xhr.ontimeout = timeoutHandler;
            xhr.onabort = abortHandler;
            xhr.onprogress = progressHandler;
            xhr.onload = loadHandler;
            //xhr.timeout = 300000;
            setupRequestHeaders(xhr, start, end);
            xhr.responseType = 'arraybuffer';
            xhr.send(null);
        } catch (err) {
            cleanup(xhr);
            fail(err);
        }
    });
}

function buildHeaders(start?: number, end?: number): HeadersInit {
    const headers: HeadersInit = {
        'X-Risr-Vers': version,
        'Cache-Control': 'no-cache, no-store, max-age=0',
        'X-Risr-Org': currentDomain,
    };
    if (safeExamBrowser) {
        headers['X-SafeExamBrowser-RequestHash'] = window.SafeExamBrowser.security.browserExamKey;
        headers['X-SafeExamBrowser-ConfigKeyHash'] = window.SafeExamBrowser.security.configKey;
    }
    if (loginId && loginPwd) {
        headers['X-Risr-User'] = loginId;
        headers['X-Risr-Pass'] = loginPwd;
    }
    if (uuid) {
        headers['X-Risr-Device-Id'] = uuid;
    }
    if (start !== undefined || end !== undefined) {
        headers['Range'] = `bytes=${start}-${end}`;
    }
    return headers;
}

export async function streamArrayBuffer({url, progress, start, end}: {url: string, progress: Progress, start: number, end: number}): Promise<ArrayBuffer> {
    // fallback to old method
    if (!window.fetch) {
        return getArrayBuffer(url, progress, start, end);
    }

    const totalBytesExpected = end - start + 1;
    const buffer = new Uint8Array(totalBytesExpected);

    let currentPosition = start;
    while (currentPosition < end) {
        const headers = buildHeaders(currentPosition, end);

        const response = await fetch(url, {
            method: 'GET',
            cache: 'no-cache',
            headers,
        });

        if (!response.ok) {
            throw new HttpError(response.status, response.statusText);
        }

        const reader = response.body?.getReader();
        if (!reader) {
            throw new Error('No readable stream body available');
        }

        const segmentStart = currentPosition;

        try {
            while (true) {
                const {value, done} = await reader.read();

                if (done) {
                    break;
                }

                if (value) {
                    const bufferOffset = currentPosition - start;
                    let bytesToWrite = value.byteLength;
                    if (bufferOffset + value.byteLength > totalBytesExpected) {
                        const overflow = (bufferOffset + bytesToWrite) - totalBytesExpected;
                        bytesToWrite -= overflow;
                        console.warn(`Truncating ${overflow} bytes to avoid buffer overflow`);
                        if (bytesToWrite <= 0) {
                            break;
                        }
                    }
                    const dataToWrite = (bytesToWrite < value.byteLength)
                        ? value.subarray(0, bytesToWrite)
                        : value;
                    console.debug('GOT', dataToWrite.byteLength, 'BYTES');
                    buffer.set(dataToWrite, bufferOffset);
                    currentPosition += dataToWrite.byteLength;
                    progress.save(dataToWrite.byteLength);
                    await progress.show();
                }
            }
            break;
        } catch (streamError) {
            if (currentPosition > segmentStart) {
                console.warn(`RETRYING stream download starting from byte ${currentPosition}`, streamError);
            } else {
                throw streamError;
            }
        }
    }
    return buffer.buffer;
}

export async function streamText({url, progress, start, end}: {url: string, progress: Progress, start: number, end: number}): Promise<string> {
    return new TextDecoder().decode(await streamArrayBuffer({url, progress, start, end}));
}

export async function streamJson({url, progress, start, end}: {url: string, progress: Progress, start: number, end: number}): Promise<unknown> {
    return safeJsonParse(await streamText({url, progress, start, end}));
}

const temporaryFailures = [500, 502, 503, 504, 507];

export async function retry<T>(fn: () => Promise<T>, attempts: number): Promise<T> {
    let delay = 100;
    while(true) {
        try {
            return await fn();
        } catch(err) {
            if (err instanceof HttpError && temporaryFailures.indexOf(err.status) > -1) {
                throw err;
            }
            if (--attempts <= 0) {
                throw err;
            }
            delay += Math.floor(delay * Math.random());
            console.warn(`Retrying after ${delay}ms due to:`, err);
            await wait(delay);
            delay *= 10;
        }
    }
}

